import router from "../index";

export const RouterHistorySearchMode = {
  ANY: 'any',
  ALL: 'all',
  SEGMENT: 'segment',
  STARTS_WITH: 'starts-with',
  STARTS_WITH_SEGMENT: 'starts-with-segment',
};

/**
 * Router hook router-history.js
 *
 * @namespace router-history
 * @date
 * @author
 * @copyright UniWeb bvba 2022
 *
 * @link https://gitlab.uniweb.be/cor/4.04-caregiver-dashboard
 *
 * @coropreventionSW [SW-4.11] app/src/APP_DASHBOARD/router/hooks/router-history.js
 * @see https://docs.google.com/document/d/1iOpHH151s_ABr-DniJIc__q-GHpXJJVq/edit#heading=h.a0z8d9xd3xr7
 *
 */

const RouterHistory = {

  /**
   * Max history size
   */
  maxHistorySize: 1000,

  /**
   * Flat history list, contains a list of the last {maxHistorySize} visited routes
   */
  history: [],
  previousRoute: {},

  /**
   * Callback for Vue.$router.afterEach
   *
   * Adds a route to the history
   * @function router-history.afterRouting
   * @param to
   * @param from
   */
  afterRouting(to, from) {
    RouterHistory._addRoute(to.path);
  },

  savePreviousRoute(from) {
    this.previousRoute = from;
  },

  /**
   * Route back to a previous page
   * @function router-history.back
   * @param options An object containing any of the following properties
   *  - steps: int
   *    The amount of pages to go back (default: 1) - this property is used by default when no other information is
   *    given
   *  - before: string
   *    Navigate to the route before a certain route match, eg. if the user visited `home`, `about/company` and is now
   *    on `about/team`, you can specify `before: '/about'` to go back to `home`
   *  - searchMode: string
   *    Can be used in combination with the `before` option, uses values from RouterHistorySearchMode and defines how
   *    the route will be matched. The searchmode will be 'starts-with-segment' when the before string starts with a
   *    slash, in all other cases it will default to 'segment'.
   * @param defaultRoute The route that should be used if no route was found in the history, same options as the
   *    `Vue.$router.push` method.
   */
  back(options, defaultRoute) {
    options = options || {};
    let redirectIndex = null;
    if (options.before) {
      const searchMode = options.searchMode || (
        options.before.substr(0, 1) === '/'
          ? RouterHistorySearchMode.STARTS_WITH_SEGMENT
          : RouterHistorySearchMode.SEGMENT
      );
      redirectIndex = this._findRouteIndexBefore(options.before, searchMode);
    } else {
      redirectIndex = this.history.length - 1 - (options.steps || 1);
    }
    if (this.history[redirectIndex]) {
      router.push(this.history[redirectIndex]);
      this.history.splice(redirectIndex);
    } else if (defaultRoute) {
      router.push(defaultRoute);
    }
  },

  _addRoute(route) {
    this.history.push(route);
    if (this.history.length > this.maxHistorySize) {
      this.history.shift();
    }
  },

  _findRouteIndexBefore(route, searchMode) {
    // trim slashes and regex escape searched route
    route = route.replace(/^\/|\/$/, '').replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    // Define search regex
    let regexStr;
    switch (searchMode) {
      case RouterHistorySearchMode.ANY:
        regexStr = route;
        break;
      case RouterHistorySearchMode.ALL:
        regexStr = '^\\/' + route + '$';
        break;
      case RouterHistorySearchMode.SEGMENT:
        regexStr = '\\/' + route + '(\\/|$)';
        break;
      case RouterHistorySearchMode.STARTS_WITH:
        regexStr = '^\\/' + route;
        break;
      case RouterHistorySearchMode.STARTS_WITH_SEGMENT:
        regexStr = '^\\/' + route + '(\\/|$)';
        break;
      default:
        console.error('Invalid search mode "' + searchMode + '" passed');
        return null;
    }
    const regex = new RegExp(regexStr);
    // Find the last non-matching history entry
    for (let i = this.history.length - 1; i >= 0; i--) {
      if (!this.history[i].match(regex)) {
        return i;
      }
    }
    return null;
  },
};

export default RouterHistory;